<template>
  <div class="status-wrapper">
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb"
      :show-powered-by="false">
      <div class="info">
        <font-awesome-icon
          @click="showVariableModal = !showVariableModal"
          class="cog-icon"
          :icon="icon.cog"/>
        <div class="screenTitle">STATUS</div>
        <div>Last update: <b>{{getDate()}}</b></div>
        <div>Time in target last 24h: <b>{{getTimeInTarget()}}</b></div>
        <XugarGauge v-if="getMaxValue" class="v-gauge"
                 :value="getValue()"
                 :initialValue="0"
                 :options="getOptions"
                 :animationSpeed="15"
                 :minValue="getMinValue"
                 :maxValue="getMaxValue"
                 gaugeValueClass="gauge-value"
                 unit="mg/dl"
                 :top="true"/>
      </div>
      <svg class="wave-container"
           viewBox="0 0 500 200"
           preserveAspectRatio="none">
        <path
          class="wave"
          d="M0.00,172.27
          C216.83,222.92
          304.30,138.39
          500.00,179.03
          L500.00,0.00
          L0.00,0.00 Z">
        </path>
      </svg>
      <span class="hero-wrapper">
        <img v-if="getValue()" class="hero" v-bind:src="getHero()"/>
      </span>
      <div class="background"/>
    </main-layout>
    <variable-selector-modal
      v-if="showVariableModal"
      v-on:close="showVariableModal = false"
      v-on:input="selectedVariable"
      :value="findVariableModelById(getXugarHeroStatus.variableId)"
      :variables="getVariablesMinimal"
      :tree="getTagsTree"
      :tagged-variables="taggedVariables"
      :selected-tags="selectedTags"/>
    <!--<xugar-menu selected-menu="XugarStatus"/>-->
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import XugarMenu from '../components/XugarMenu';
import pointerImage from '../assets/xugarHero/triangle.png';
import {mapActions, mapGetters} from 'vuex';
import dayjs from 'dayjs';
import VariableSelectorModal from '@/components/variable/VariableSelectorModal';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import variableApi from '@/api/variable';
import {
  faCog
} from '@fortawesome/pro-regular-svg-icons';
import XugarGauge from 'BRAND/components/XugarGauge';

export default {
  name: 'XugarStatus',
  components: {
    XugarMenu,
    MainLayout,
    VariableSelectorModal,
    FontAwesomeIcon,
    XugarGauge
  },
  data () {
    return {
      state: {},
      icon: {
        cog: faCog
      },
      showVariableModal: false,
      selectedVariables: [],
      modalMultipleSelection: false,
      selectedTags: [],
      variablesMinimal: [],
      taggedVariables: []
    };
  },
  created () {
    this.retrieveTagsTree({ context: this });
    this.retrieveVariables({ context: this });
    this.startRetrieveXugarHeroStatus({ context: this });
    variableApi.getVariables(this).then(data => {
      this.taggedVariables = this.variablesMinimal = data.body.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'});
      });
      this.onRetrieveVariables();
    });
  },
  destroyed () {
    this.stopRetrieveXugarHeroStatus({ context: this });
  },
  mounted () {
    this.state = this.getXugarHeroStatus;
  },
  computed: {
    ...mapGetters([
      'getVariables',
      'getTagsTree',
      'getXugarHeroStatus'
    ]),
    getBreadcrumb () {
      const self = 'Status';
      return [
        {name: self, link: 'xugar/status', disabled: true}
      ];
    },
    getOptions () {
      const upperCritical = this.getXugarHeroStatus.glucoseThresholds !== null ? this.getXugarHeroStatus.glucoseThresholds.upperCriticalThreshold : 0;
      const lowerCritical = this.getXugarHeroStatus.glucoseThresholds !== null ? this.getXugarHeroStatus.glucoseThresholds.lowerCriticalThreshold : 0;
      const upperWarning = this.getXugarHeroStatus.glucoseThresholds !== null ? this.getXugarHeroStatus.glucoseThresholds.upperWarningThreshold : 0;
      const lowerWarning = this.getXugarHeroStatus.glucoseThresholds !== null ? this.getXugarHeroStatus.glucoseThresholds.lowerWarningThreshold : 0;

      return {
        angle: -0.18,
        lineWidth: 0.08,
        limitMax: false,
        limitMin: false,
        pointer: {
          iconPath: pointerImage,
          iconScale: 1,
          length: 0.39,
          strokeWidth: 0,
          color: '#FFFFFF'
        },
        staticZones: [
          {strokeStyle: '#FFFFFF40', min: this.getMinValue, max: lowerCritical},
          {strokeStyle: '#FFFFFF80', min: lowerCritical, max: lowerWarning},
          {strokeStyle: '#FFFFFF', min: lowerWarning, max: upperWarning},
          {strokeStyle: '#FFFFFF80', min: upperWarning, max: upperCritical},
          {strokeStyle: '#FFFFFF40', min: upperCritical, max: this.getMaxValue}
        ],
        radiusScale: 0.7,
        staticLabels: {
          font: '1em Nunito, sans-serif',
          labels: [lowerCritical, lowerWarning, upperWarning, upperCritical],
          color: 'white',
          fractionDigits: 0
        }
      };
    },
    getMaxValue () {
      if (this.getXugarHeroStatus.glucoseThresholds !== null) {
        if (this.getXugarHeroStatus.lastGloucoseRead > parseInt(this.getXugarHeroStatus.glucoseThresholds.maximum) + 30) {
          return this.getXugarHeroStatus.lastGloucoseRead;
        }
        return parseInt(this.getXugarHeroStatus.glucoseThresholds.maximum) + 30;
      }
      return 0;
    },
    getMinValue () {
      if (this.getXugarHeroStatus.glucoseThresholds !== null) {
        if (this.getXugarHeroStatus.lastGloucoseRead < parseInt(this.getXugarHeroStatus.glucoseThresholds.minimum) - 25) {
          return this.getXugarHeroStatus.lastGloucoseRead;
        }
        return parseInt(this.getXugarHeroStatus.glucoseThresholds.minimum) - 25;
      }
      return 0;
    },
    getVariablesMinimal () {
      return this.variablesMinimal;
    },
    getTaggedVariables () {
      const variablesInUse = this.selectedVariables.filter(selected => selected.model).map(o => o.model.id);
      return this.taggedVariables.filter(taggedVar => !variablesInUse.includes(taggedVar.id));
    }
  },
  methods: {
    ...mapActions([
      'startRetrieveXugarHeroStatus',
      'stopRetrieveXugarHeroStatus',
      'updateTrackingVariable',
      'retrieveVariables',
      'retrieveTagsTree',
      'retrieveXugarHeroStatus'
    ]),
    getHero () {
      let upperCritical = 0;
      let lowerCritical = 0;
      let upperWarning = 0;
      let lowerWarning = 0;
      let value = 0;
      if (this.getXugarHeroStatus.glucoseThresholds !== null) {
        upperCritical = parseInt(this.getXugarHeroStatus.glucoseThresholds.upperCriticalThreshold);
        lowerCritical = parseInt(this.getXugarHeroStatus.glucoseThresholds.lowerCriticalThreshold);
        upperWarning = parseInt(this.getXugarHeroStatus.glucoseThresholds.upperWarningThreshold);
        lowerWarning = parseInt(this.getXugarHeroStatus.glucoseThresholds.lowerWarningThreshold);
        value = parseFloat(this.getXugarHeroStatus.lastGloucoseRead).toFixed(2);
      }
      let attitude = 'happy';
      if (value > upperWarning) {
        if (value > upperCritical) {
          attitude = 'criticalHigh';
        } else {
          attitude = 'warningHigh';
        }
      } else if (value < lowerWarning) {
        if (value < lowerCritical) {
          attitude = 'criticalLow';
        } else {
          attitude = 'warningLow';
        }
      }
      const url = require('BRAND/assets/xugarHero/XugarHero_' + attitude + '.svg');
      return url;
    },
    getDate () {
      return dayjs(this.getXugarHeroStatus.lastUpdate).format('HH:mm') + ' | ' + dayjs(this.getXugarHeroStatus.lastUpdate).format('MM/DD/YYYY');
    },
    getTimeInTarget () {
      if (this.getXugarHeroStatus.timeInTarget !== null) {
        return parseFloat(this.getXugarHeroStatus.timeInTarget).toFixed(2) + ' %';
      }
      return ' - ';
    },
    getValue () {
      if (this.getXugarHeroStatus.lastGloucoseRead !== null) {
        return this.getXugarHeroStatus.lastGloucoseRead;
      }
      return 0;
    },
    selectedVariable (variableId) {
      this.updateTrackingVariable({context: this, variableId: variableId.id}).then(() => {
        this.retrieveXugarHeroStatus({ context: this });
      });
    },
    findVariableModelById (id) {
      const variables = this.getVariablesMinimal.slice();
      for (let i = 0; i < variables.length; i++) {
        if (Number(id) === variables[i].id) {
          return variables[i];
        }
      }
      return undefined;
    },
    onRetrieveVariables () {
      const query = this.$route.query;

      // Variables
      const variableIds = query.variables;
      if (variableIds) {
        const variableArray = variableIds.split(',');
        for (let i = 0; i < variableArray.length; i++) {
          const id = variableArray[i];
          this.selectedVariables.push({
            options: this.getVariablesForNewSelector(),
            model: this.findVariableModelById(id)
          });
        }
      } else {
        this.selectedVariables.push({
          options: this.getVariablesForNewSelector(),
          model: undefined
        });
      }
    },
    getVariablesForNewSelector () {
      const variables = this.getVariablesMinimal.slice();
      const size = this.selectedVariables.length;
      for (let i = 0; i < size; i++) {
        const selectedVariableModel = this.selectedVariables[i].model;
        if (selectedVariableModel) {
          // Remove previously selected variable
          const id = selectedVariableModel.id;
          let position = 0;
          for (let j = 0; j < variables.length; j++) {
            if (variables[j].id === id) {
              position = j;
              break;
            }
          }
          variables.splice(position, 1);
        }
      }
      return variables;
    }
  }
};
</script>

<style lang="scss" scoped>
  @import "../../../stylesheets/variables.scss";

  .background {
    height: 100%;
    background-image: linear-gradient(#990038, 30%, #E66327);
  }

  .status-wrapper {
    height: 100%;
    position: absolute;
    width: 100%;
  }

  .wave-container {
    height: 41%;
    width: 100%;
    position: absolute;
    bottom: 0;
    -webkit-transform: rotate(180deg);     /* Chrome and other webkit browsers */
    -moz-transform: rotate(180deg);        /* FF */
    -o-transform: rotate(180deg);          /* Opera */
    -ms-transform: rotate(180deg);         /* IE9 */
    transform: rotate(180deg);
      @media (min-width: $screen-xxl-max) {
        height: 45%;
      }
    }

  .wave {
    stroke: none;
    fill: $white;
  }

  .wrapper {
    position: relative;
  }

  .info {
    position: absolute;
    color: $white;
    width: 100%;
    text-align: center;
    margin-top: $sp-margin-small;
  }

  .screenTitle {
    margin-top: $sp-margin-small;
  }

  .v-gauge {
    margin-top: $sp-margin-larger;
    @media (max-width: $screen-xxl) {
      margin-top: $sp-margin-small;
    }
    @media (max-width: $screen-xl) {
      margin-top: -$sp-margin-large;
    }
    @media (max-width: $screen-l) {
      margin-top: -$sp-margin-larger;
    }
  }

  .hero {
    height: 90%;
    bottom: 18%;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }

  .hero-wrapper {
    display: block;
    width: 100%;
    height: 55%;
    bottom: 0;
    position: absolute;
  }

  .cog-icon {
    font-size: $fs-2xl;
    color: $white-trans3;
    position: absolute;
    right: $sp-margin-small;
  }

  .cog-icon:hover {
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/brand/xugarhero/layouts/XugarStatus.vue